import { loginGuard, loginWorkspaceGuard } from "src/router/guards/manager/auth"
import { UUID_REGEX } from "src/util/constants/configurations"
import { NamedManagerPath } from "src/util/enums/namedPaths"
import { RouteRecordRaw } from "vue-router"

/**
 * All the routes that are available to a manager.
 *
 * We have an entry per route that specifies the *full path* in the `path` field, so no subrouting.
 * This has some benefits:
 *    - We can use the appropriate layout for each route.
 *    - We can adjust permissions on the route level. That is, we can be very granular.
 *    - There's a single spot where all the details of a route can be seen (parameters, permissions, name, layout).
 *      This way we can be confident when we want to modify routes.
 *
 *  We always name uuid's that are captured in the path properly, so we know what they refer to.
 *  For instance, prefer `:workspaceId` over the generic `:id`.
 */
const routesManager: RouteRecordRaw[] = [
  ///// AUTH /////
  {
    path: "/auth",
    component: () => import("src/layouts/manager/AuthLayout.vue"),
    beforeEnter: [loginWorkspaceGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.INITIATE_APP,
        component: () => import("src/pages/manager/InitiateApp.vue"),
      },
    ],
  },

  ///// WORKSPACES /////
  {
    path: "/workspaces",
    component: () => import("src/layouts/manager/WorkspaceLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.SELECT_WORKSPACE,
        component: () => import("src/pages/manager/workspace/WorkspaceSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.WORKSPACE,
        component: () => import("src/pages/manager/workspace/WorkspaceContainer.vue"),
      },
    ],
  },
  ///// ISSUES /////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/issues`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.SELECT_ISSUE,
        component: () => import("src/pages/manager/issue/IssueSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/issues/:issueId${UUID_REGEX}`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.ISSUE,
        component: () => import("src/pages/manager/issue/IssueContainer.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/issues/create/:itemId${UUID_REGEX}/:closedIssueId${UUID_REGEX}?`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.CREATE_ISSUE,
        component: () => import("src/pages/manager/issue/IssueCreate.vue"),
      },
    ],
  },
  ///// ITEMS /////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/items`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.SELECT_ITEM,
        component: () => import("src/pages/manager/item/ItemSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/items/:itemId${UUID_REGEX}`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.ITEM,
        component: () => import("src/pages/manager/item/ItemContainer.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/items/create/:clonedItemId${UUID_REGEX}?`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.CREATE_ITEM,
        component: () => import("src/pages/manager/item/ItemCreate.vue"),
      },
    ],
  },

  ///// PERFORMED WORK /////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/corrective`,
    redirect: { name: NamedManagerPath.SELECT_PERFORMED_WORK },
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/regular`,
    redirect: { name: NamedManagerPath.SELECT_PERFORMED_WORK },
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.SELECT_PERFORMED_WORK,
        component: () => import("src/pages/manager/work/PerformedWorkSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/corrective/:performedWorkId${UUID_REGEX}`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.PERFORMED_CORRECTIVE_WORK,
        component: () => import("src/pages/manager/work/PerformedCorrectiveContainer.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/performed-work/regular/:performedWorkId${UUID_REGEX}`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.PERFORMED_REGULAR_WORK,
        component: () => import("src/pages/manager/work/PerformedRegularContainer.vue"),
      },
    ],
  },

  ///// WORK ORDERS /////
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders/regular`,
    redirect: { name: NamedManagerPath.SELECT_WORK_ORDER },
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders/corrective`,
    redirect: { name: NamedManagerPath.SELECT_WORK_ORDER },
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.SELECT_WORK_ORDER,
        component: () => import("src/pages/manager/work-order/WorkOrderSelect.vue"),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders/corrective/:workOrderId${UUID_REGEX}/:draftCreated?`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.WORK_ORDER_CORRECTIVE,
        component: () => import("src/pages/manager/work-order/WorkOrderCorrectiveContainer.vue"),
        props: (route) => ({ draftCreated: Boolean(route.params.draftCreated) }),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders/regular/:workOrderId${UUID_REGEX}/:draftCreated?`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.WORK_ORDER_REGULAR,
        component: () => import("src/pages/manager/work-order/WorkOrderRegularContainer.vue"),
        props: (route) => ({ draftCreated: Boolean(route.params.draftCreated) }),
      },
    ],
  },
  {
    path: `/workspaces/:workspaceId${UUID_REGEX}/work-orders/create/:itemId${UUID_REGEX}?:issueId${UUID_REGEX}?`,
    component: () => import("src/layouts/manager/GenericLayout.vue"),
    beforeEnter: [loginGuard],
    children: [
      {
        path: "",
        name: NamedManagerPath.CREATE_WORK_ORDER,
        component: () => import("src/pages/manager/work-order/WorkOrderCreate.vue"),
      },
    ],
  },
]

export default routesManager
